import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { EventCoachRanking } from "@/components/EventCoachRanking";
import { Trophy, Save, Settings2, Star, Sparkles, FileDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONFIG = {
  p_1st: 10,
  p_2nd: 7,
  p_3rd: 5,
  p_overall: 20,
  p_best_poser: 10,
  bonus_1st_team: 30,
  bonus_2nd_team: 20,
  bonus_3rd_team: 10,
  best_poser_athlete_ids: [],
  overall_winner_athlete_ids: [],
};

export default function TeamRanking() {
  const { currentId } = useCompetition();
  const [data, setData] = useState({ teams: [], config: DEFAULT_CONFIG });
  const [athletes, setAthletes] = useState([]);
  const [coachData, setCoachData] = useState({ coaches: [], competition_name: "" });
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    if (!currentId) return;
    try {
      const [r, a, c] = await Promise.all([
        api.get(`/competitions/${currentId}/team-ranking`),
        api.get(`/competitions/${currentId}/athletes`),
        api.get(`/competitions/${currentId}/coach-ranking`),
      ]);
      setData(r.data);
      setCfg({ ...DEFAULT_CONFIG, ...r.data.config });
      setAthletes(a.data);
      setCoachData(c.data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
    // Poll every 10s for live updates
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [currentId]);

  const saveConfig = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put(`/competitions/${currentId}/points-config`, cfg);
      setMsg("✓ Configuração salva");
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const setNum = (k, v) => {
    const n = parseInt(v, 10);
    setField(k, Number.isNaN(n) ? 0 : n);
  };

  const athleteMap = useMemo(
    () => Object.fromEntries(athletes.map((a) => [a.id, a])),
    [athletes]
  );

  const toggleAthleteInList = (listName, aid) => {
    const list = cfg[listName] || [];
    const next = list.includes(aid) ? list.filter((x) => x !== aid) : [...list, aid];
    setCfg({ ...cfg, [listName]: next });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-1">
            <Trophy size={12} /> Pontuação por equipe
          </div>
          <h1 className="font-display text-5xl font-black tracking-tight">
            RANKING DE EQUIPES
          </h1>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            Atualização automática a cada 10 segundos
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CompetitionSelector />
          <button
            data-testid="btn-export-team-pdf"
            className="btn-ghost inline-flex items-center gap-2"
            onClick={() => {
              if (!currentId || data.teams.length === 0) return;
              // Same-origin (via ingress) GET with httpOnly cookies auto-attached.
              // Matches the pattern used by /ranking, /sumulas and /roteiro PDF exports.
              window.open(
                `${BACKEND}/api/competitions/${currentId}/team-ranking.pdf`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
            disabled={!currentId || data.teams.length === 0}
          >
            <FileDown size={14} /> Exportar PDF
          </button>
          <button
            data-testid="btn-toggle-config"
            className="btn-ghost inline-flex items-center gap-2"
            onClick={() => setShowConfig((v) => !v)}
          >
            <Settings2 size={14} /> {showConfig ? "Fechar config" : "Configurar pontos"}
          </button>
        </div>
      </div>

      {showConfig && (
        <div className="card p-5 mb-6" data-testid="config-panel">
          <div className="overline mb-3">Configuração de pontos e bônus</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {[
              ["p_1st", "1º lugar"],
              ["p_2nd", "2º lugar"],
              ["p_3rd", "3º lugar"],
              ["p_overall", "Overall"],
              ["p_best_poser", "Best Poser"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="overline block mb-1">{label}</label>
                <input
                  type="number"
                  data-testid={`cfg-${k}`}
                  className="input-field tabular-nums"
                  value={cfg[k]}
                  onChange={(e) => setNum(k, e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="overline mb-2">Bônus de participação (top-3 equipes por nº inscritos)</div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              ["bonus_1st_team", "1ª equipe"],
              ["bonus_2nd_team", "2ª equipe"],
              ["bonus_3rd_team", "3ª equipe"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="overline block mb-1">{label}</label>
                <input
                  type="number"
                  data-testid={`cfg-${k}`}
                  className="input-field tabular-nums"
                  value={cfg[k]}
                  onChange={(e) => setNum(k, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="overline mb-2 flex items-center gap-1">
                <Star size={11} /> Vencedores Overall
              </div>
              <div
                data-testid="overall-picker"
                className="max-h-40 overflow-y-auto border border-white/10 p-2 text-xs space-y-1"
              >
                {athletes.length === 0 ? (
                  <div className="text-[var(--text-secondary)]">Nenhum atleta cadastrado.</div>
                ) : (
                  athletes.map((a) => (
                    <label
                      key={a.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-1 py-0.5"
                    >
                      <input
                        type="checkbox"
                        className="accent-[#C9A84C]"
                        checked={cfg.overall_winner_athlete_ids.includes(a.id)}
                        onChange={() => toggleAthleteInList("overall_winner_athlete_ids", a.id)}
                      />
                      <span>
                        {a.numero} · {a.nome}{" "}
                        <span className="text-[var(--text-secondary)]">
                          ({a.equipe || "sem equipe"})
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div>
              <div className="overline mb-2 flex items-center gap-1">
                <Sparkles size={11} /> Best Poser
              </div>
              <div
                data-testid="best-poser-picker"
                className="max-h-40 overflow-y-auto border border-white/10 p-2 text-xs space-y-1"
              >
                {athletes.length === 0 ? (
                  <div className="text-[var(--text-secondary)]">Nenhum atleta cadastrado.</div>
                ) : (
                  athletes.map((a) => (
                    <label
                      key={a.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-1 py-0.5"
                    >
                      <input
                        type="checkbox"
                        className="accent-[#C9A84C]"
                        checked={cfg.best_poser_athlete_ids.includes(a.id)}
                        onChange={() => toggleAthleteInList("best_poser_athlete_ids", a.id)}
                      />
                      <span>
                        {a.numero} · {a.nome}{" "}
                        <span className="text-[var(--text-secondary)]">
                          ({a.equipe || "sem equipe"})
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="cfg-save"
              className="btn-primary inline-flex items-center gap-2"
              onClick={saveConfig}
              disabled={saving}
            >
              <Save size={14} />
              {saving ? "Salvando..." : "SALVAR CONFIGURAÇÃO"}
            </button>
            {msg && (
              <span className="text-xs text-[#5AF78E]">{msg}</span>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th className="w-14">Pos</th>
              <th>Equipe</th>
              <th className="text-center">Inscritos</th>
              <th className="text-center">1º</th>
              <th className="text-center">2º</th>
              <th className="text-center">3º</th>
              <th className="text-center">Overall</th>
              <th className="text-center">Best Poser</th>
              <th className="text-center">Bônus</th>
              <th className="text-right pr-4">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.teams.map((t) => (
              <tr key={t.nome} data-testid={`team-row-${t.nome}`}>
                <td className="tabular-nums font-display font-black text-2xl text-[#C9A84C]">
                  {t.posicao}
                </td>
                <td className="font-semibold">{t.nome}</td>
                <td className="text-center tabular-nums">{t.inscritos}</td>
                <td className="text-center tabular-nums">{t.primeiros}</td>
                <td className="text-center tabular-nums">{t.segundos}</td>
                <td className="text-center tabular-nums">{t.terceiros}</td>
                <td className="text-center tabular-nums">{t.overalls}</td>
                <td className="text-center tabular-nums">{t.best_posers}</td>
                <td className="text-center tabular-nums">{t.pontos_bonus}</td>
                <td className="text-right pr-4 tabular-nums font-display font-black text-2xl">
                  {t.total}
                </td>
              </tr>
            ))}
            {data.teams.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-8 text-[var(--text-secondary)]">
                  Nenhuma equipe com atletas ainda. Cadastre atletas com o campo{" "}
                  <b>Equipe</b> preenchido.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[var(--text-secondary)] mt-3">
        Desempates: <b>total</b> → <b>maior nº de 1º lugares</b> →{" "}
        <b>títulos Overall</b> → nome (alfabético).
      </div>

      <EventCoachRanking coaches={coachData.coaches} competitionName={coachData.competition_name} />
    </div>
  );
}
